import { Loader, ReactDropdown } from '@storybook';
import env from '@beam-australia/react-env';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Header } from 'views/header';
import { ApiConsole } from 'views/api-console';
import './document.scss';

/* The published reference's host. Env-driven so a local docs checkout (a
   GitBook export on :3456, say) can be framed instead of the live site, which
   the hardcoded URL made impossible. Trailing slash trimmed — the paths below
   bring their own. */
const DOCS_URL = (env('DOCS_URL') || 'https://docs.simplici.io').replace(
	/\/$/,
	''
);

/**
 * THE API DOCS, PER VERSION.
 *
 * v2 is FROZEN — the surface every existing integration is built against, and
 * the link below is the one this page has always opened. v3 is additive and
 * lives beside it; nothing here changes what a v2 reader sees.
 *
 * v2's `href` points at the published reference and renders as an iframe. v3
 * has no separate reference to link to — it renders the "try it" console
 * (`ApiConsole`) instead, which reads the live contract itself
 * (`GET {base}/openapi.json`) rather than a hand-written page, so `href` for
 * v3 stays empty on purpose: it is a marker for "not an iframe", not a
 * placeholder for a missing document.
 */
type ApiVersion = 'v2' | 'v3';

const DOCS: Record<ApiVersion, { label: string; href: string }> = {
	v2: {
		label: 'API v2',
		href: `${DOCS_URL}/simplici/getting-started`,
	},
	v3: {
		label: 'API v3',
		href: '',
	},
};

const VERSIONS = Object.keys(DOCS) as ApiVersion[];

/* The reader's choice, so opening the page again lands where they left it.
   Per browser, per device: it is a preference, not account state. */
const STORE_KEY = 'simplici-api-docs-version';

/* /api?v=v3 — a link that pins the version it opens on, so one can be handed
   out (from the eVault deck, a ticket, a release note) and land where it says.
   It beats the stored choice but never replaces it: following somebody else's
   link must not change which version this reader gets next time. */
const QUERY_KEY = 'v';

const storedVersion = (): ApiVersion => {
	try {
		const v = localStorage.getItem(STORE_KEY) as ApiVersion | null;
		return v && DOCS[v] ? v : 'v2';
	} catch {
		/* private window, blocked site data: fall back to the frozen surface. */
		return 'v2';
	}
};

export const Document = () => {
	const [isLoading, setIsLoadng] = useState(true);
	const [searchParams, setSearchParams] = useSearchParams();

	const pinned = searchParams.get(QUERY_KEY) as ApiVersion | null;
	const pinnedVersion = pinned && DOCS[pinned] ? pinned : null;

	const [version, setVersion] = useState<ApiVersion>(
		() => pinnedVersion ?? storedVersion()
	);

	/* The pin can also arrive at a page that is already mounted — a second link
	   followed from the same tab. Reading the URL on mount alone would show the
	   version the reader left behind. */
	useEffect(() => {
		if (!pinnedVersion || pinnedVersion === version) return;
		setVersion(pinnedVersion);
		setIsLoadng(!!DOCS[pinnedVersion].href);
	}, [pinnedVersion, version]);

	const options = useMemo(
		() => VERSIONS.map(v => ({ label: DOCS[v].label, value: v })),
		[]
	);

	const handleVersion = useCallback(
		(option: { value: ApiVersion }) => {
			const next = option?.value;
			if (!next || !DOCS[next]) return;
			setVersion(next);
			setIsLoadng(!!DOCS[next].href);
			try {
				localStorage.setItem(STORE_KEY, next);
			} catch {
				/* not being able to remember the choice must not break the page. */
			}
			/* Keep the address bar showing what is on screen, so the page is
			   always copy-pasteable as a link. Replaced, not pushed: a version
			   switch is not a step the Back button should retrace. */
			const params = new URLSearchParams(searchParams);
			params.set(QUERY_KEY, next);
			setSearchParams(params, { replace: true });
		},
		[searchParams, setSearchParams]
	);

	const doc = DOCS[version];
	const isV3 = version === 'v3';

	return (
		/* `sv2-embed-scope` ON THE WRAPPER, not inside the console.
		   This page is the one redesigned surface that does NOT own its route:
		   it renders under the panel's own fixed header band, so the header sits
		   OUTSIDE `.sv2-scope` and kept reading the light `--color-*` family
		   while the console below it went dark — a white strip across the top of
		   a dark page. The class is the same remap `.sidebar-container` uses to
		   follow a redesigned page into dark (tokens.scss), applied here to the
		   chrome that wraps this one. Dark-only by construction: the remap is
		   scoped to `[data-sv2-theme='dark']`, which the console claims while it
		   is mounted, so the v2 iframe view is untouched. */
		<div
			className={`wrapper-iframe${
				isV3 ? ' wrapper-iframe--console sv2-embed-scope' : ''
			}`}
		>
			{isLoading && doc.href && <Loader />}
			<div className="outer--iframe">
				<div className="inner--iframe">
					<Header title="API Docs">
						<div className="api-version" data-qa="api-version">
							<ReactDropdown
								options={options}
								value={{ label: doc.label, value: version }}
								handleChangeSelect={handleVersion}
								isSearchable={false}
							/>
						</div>
					</Header>
				</div>
			</div>
			{isV3 ? (
				<div className="api-version__console">
					<ApiConsole />
				</div>
			) : doc.href ? (
				<iframe
					key={version}
					src={doc.href}
					title={`Simplici ${doc.label}`}
					onLoad={() => setIsLoadng(false)}
					className="Document--iframe"
				/>
			) : (
				<div className="api-version__pending">
					<div className="api-version__pending-title">
						{doc.label} documentation is not published yet
					</div>
				</div>
			)}
		</div>
	);
};

export default Document;
